/* Playmo Generation · configurador de opciones con suplementos.
   - Muestra/oculta grupos según «Mostrar solo si» (Grupo=Valor o Grupo!=Valor).
   - Calcula el total: precio de la variante + suplementos, por la cantidad.
   - En modo «paso a paso» cada grupo es un desplegable: al elegir se cierra y se abre el siguiente.
   - Nada viene marcado: no deja añadir a la cesta hasta elegir en todos los grupos obligatorios visibles.
   - Paso de texto (data-text: nombre, dorsal, fecha…): está completo cuando todos sus campos tienen texto.
     Se oculta con las variantes de data-hide-variants («Sin personalizar»). Lo escrito sale en «Tu figura».
   - Al añadir a la cesta, primero añade las variantes de suplemento y luego deja
     que el product-form de Dawn añada la figura (con sus elecciones como propiedades). */
if (!customElements.get('pg-options')) {
  customElements.define(
    'pg-options',
    class PgOptions extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.form = document.getElementById(this.formId);
        this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
        this.baseLabel = this.dataset.baseLabel || '';
        this.variantId = this.form?.querySelector('[name="id"]')?.value || '';
        this.groups = Array.from(this.querySelectorAll('[data-group]'));
        this.linesEl = this.querySelector('[data-lines]');
        this.totalEl = this.querySelector('[data-total]');
        this.errorEl = this.querySelector('[data-error]');
        this.errorPrefix = this.errorEl?.textContent.trim() || '';
        this.money = new Intl.NumberFormat(this.dataset.locale || 'es', {
          style: 'currency',
          currency: this.dataset.currency || 'EUR',
        });

        this.steps = this.dataset.layout === 'steps';
        this.addEventListener('change', (event) => {
          this.update();
          const group = event.target.closest('[data-group]');
          if (group && this.hasValue(group)) group.classList.remove('is-missing');
          if (this.errorEl && !this.errorEl.hidden) this.showMissing(this.missingGroups(), false);
          // En el paso de texto solo se avanza cuando están rellenos todos los campos
          if (this.steps && group && (!('text' in group.dataset) || this.hasValue(group))) this.advance(group);
        });
        // Al escribir, se actualiza «Tu figura» sin esperar a salir del campo
        this.addEventListener('input', (event) => {
          if (!event.target.matches('input[type="text"]')) return;
          this.update(false);
          const group = event.target.closest('[data-group]');
          if (group && this.hasValue(group)) group.classList.remove('is-missing');
        });
        this.quantityInput = document.querySelector(`input[name="quantity"][form="${this.formId}"]`);
        this.quantityInput?.addEventListener('change', () => this.update());
        this.quantityInput
          ?.closest('quantity-input')
          ?.addEventListener('click', () => requestAnimationFrame(() => this.update()));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const info = this.closest('product-info');
            if (!data?.variant || (info && data.sectionId !== info.dataset.section)) return;
            this.basePrice = data.variant.price;
            this.baseLabel = data.variant.title;
            this.variantId = String(data.variant.id);
            const before = this.groups.filter((g) => !g.hidden);
            this.update();
            // Si al pasar a «Personalizado» aparece el paso de texto, se abre
            const shown = this.groups.find((g) => 'text' in g.dataset && !g.hidden && !before.includes(g));
            const details = shown?.querySelector('details');
            if (details) details.open = true;
          });
        }

        this.productForm = this.form?.closest('product-form');
        this.onSubmit = this.onSubmit.bind(this);
        // Fase de captura en el ancestro: se ejecuta antes que el listener de Dawn en el <form>.
        this.productForm?.addEventListener('submit', this.onSubmit, true);

        this.update();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.productForm?.removeEventListener('submit', this.onSubmit, true);
      }

      valueOf(groupName) {
        const group = this.groups.find((g) => g.dataset.group === groupName);
        if (!group || group.hidden) return null;
        const checked = group.querySelector('input:checked, select');
        return checked && checked.value !== '' ? checked.value : null;
      }

      hasValue(group) {
        if ('text' in group.dataset) {
          const inputs = Array.from(group.querySelectorAll('input[type="text"]'));
          return inputs.length > 0 && inputs.every((input) => input.value.trim() !== '');
        }
        const checked = group.querySelector('input:checked, select');
        return Boolean(checked && checked.value !== '');
      }

      missingGroups() {
        return this.groups.filter((g) => !g.hidden && 'required' in g.dataset && !this.hasValue(g));
      }

      showMissing(missing, focus = true) {
        this.groups.forEach((g) => g.classList.toggle('is-missing', missing.includes(g)));
        if (!this.errorEl) return;
        this.errorEl.hidden = missing.length === 0;
        this.errorEl.textContent = `${this.errorPrefix} ${missing.map((g) => g.dataset.group).join(', ')}.`;
        if (!focus || missing.length === 0) return;
        const first = missing[0];
        const details = first.querySelector('details');
        if (details) details.open = true;
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const empty = Array.from(first.querySelectorAll('input[type="text"]')).find((i) => !i.value.trim());
        (empty || first.querySelector('input, select'))?.focus({ preventScroll: true });
      }

      isVisible(group) {
        const hideFor = group.dataset.hideVariants;
        if (hideFor && this.variantId && hideFor.includes(`,${this.variantId},`)) return false;
        const rule = group.dataset.showIf;
        if (!rule) return true;
        const negate = rule.includes('!=');
        const [name, expected] = rule.split(negate ? '!=' : '=').map((part) => part.trim());
        const alternatives = expected.split('|').map((v) => v.trim());
        const current = this.valueOf(name);
        if (current === null) return false;
        return negate ? !alternatives.includes(current) : alternatives.includes(current);
      }

      selectedAddons() {
        const addons = [];
        this.groups.forEach((group) => {
          if (group.hidden) return;
          const input = group.querySelector('input:checked') || group.querySelector('select');
          const source = input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
          if (source?.dataset.addon) {
            addons.push({
              id: Number(source.dataset.addon),
              price: parseInt(source.dataset.price, 10) || 0,
              group: group.dataset.group,
              label: source.value,
            });
          }
        });
        return addons;
      }

      advance(group) {
        if (!group) return;
        const visible = this.groups.filter((g) => !g.hidden);
        const next = visible[visible.indexOf(group) + 1];
        if (!next) return;
        const current = group.querySelector('details');
        const target = next.querySelector('details');
        if (current) current.open = false;
        if (target) {
          target.open = true;
          // Que el paso siguiente quede a la vista sin saltos bruscos
          const rect = target.getBoundingClientRect();
          if (rect.top < 80 || rect.top > window.innerHeight - 120) {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }

      textValues(group) {
        return Array.from(group.querySelectorAll('input[type="text"]'))
          .filter((input) => input.value.trim() !== '')
          .map((input) => ({ label: input.dataset.label, value: input.value.trim() }));
      }

      update(flash = true) {
        // Visibilidad (dos pasadas para dependencias encadenadas)
        for (let pass = 0; pass < 2; pass++) {
          this.groups.forEach((group) => {
            const visible = this.isVisible(group);
            group.hidden = !visible;
            group.querySelectorAll('input, select').forEach((el) => (el.disabled = !visible));
          });
        }

        // Numeración de los pasos visibles
        this.groups
          .filter((group) => !group.hidden)
          .forEach((group, index) => {
            const num = group.querySelector('[data-step-num]');
            if (num) num.textContent = index + 1;
          });

        // Valor elegido junto al título del grupo
        this.groups.forEach((group) => {
          const target = group.querySelector('[data-selected]');
          if ('text' in group.dataset) {
            const values = this.textValues(group).map((t) => t.value);
            if (target) target.textContent = values.length ? `· ${values.join(' · ')}` : '';
            return;
          }
          const input = group.querySelector('input:checked') || group.querySelector('select');
          if (target) target.textContent = input && input.value !== '' ? `· ${input.value}` : '';
        });

        const quantity = Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
        const addons = this.selectedAddons();
        const unit = this.basePrice + addons.reduce((sum, a) => sum + a.price, 0);

        const lines = [
          `<li><span>${this.escape(this.dataset.productTitle)}${this.baseLabel && this.baseLabel !== 'Default Title' ? ` · ${this.escape(this.baseLabel)}` : ''}</span><span>${this.money.format(this.basePrice / 100)}</span></li>`,
          ...addons.map(
            (a) => `<li><span>${this.escape(a.group)}: ${this.escape(a.label)}</span><span>+${this.money.format(a.price / 100)}</span></li>`
          ),
          ...this.groups
            .filter((g) => 'text' in g.dataset && !g.hidden)
            .flatMap((g) => this.textValues(g))
            .map((t) => `<li><span>${this.escape(t.label)}: ${this.escape(t.value)}</span><span></span></li>`),
        ];
        if (quantity > 1) lines.push(`<li class="pg-options__qty"><span>× ${quantity}</span><span></span></li>`);
        this.linesEl.innerHTML = lines.join('');
        this.totalEl.textContent = this.money.format((unit * quantity) / 100);
        if (!flash) return;
        this.classList.add('is-updated');
        clearTimeout(this.flash);
        this.flash = setTimeout(() => this.classList.remove('is-updated'), 400);
      }

      escape(text) {
        const div = document.createElement('div');
        div.textContent = text || '';
        return div.innerHTML;
      }

      async onSubmit(event) {
        if (this.passThrough) {
          this.passThrough = false;
          return;
        }
        const missing = this.missingGroups();
        if (missing.length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showMissing(missing);
          return;
        }

        const addons = this.selectedAddons();
        if (addons.length === 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const submit = this.form.querySelector('[type="submit"]');
        if (submit?.getAttribute('aria-disabled') === 'true') return;
        submit?.classList.add('loading');

        const quantity = Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
        const figure = `${this.dataset.productTitle}${this.baseLabel && this.baseLabel !== 'Default Title' ? ` (${this.baseLabel})` : ''}`;
        try {
          const response = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: addons.map((a) => ({ id: a.id, quantity, properties: { [this.dataset.forLabel || 'Para']: figure } })),
            }),
          });
          const data = await response.json();
          if (data.status) throw new Error(data.description || data.message);
        } catch (error) {
          submit?.classList.remove('loading');
          const wrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
          const message = this.productForm.querySelector('.product-form__error-message');
          if (wrapper && message) {
            message.textContent = error.message;
            wrapper.hidden = false;
          }
          return;
        }

        submit?.classList.remove('loading');
        this.passThrough = true;
        this.form.requestSubmit(submit || undefined);
      }
    }
  );
}
